import {
  Fragment,
  type JSX,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react';

import { Sinnbild, type NamenVon } from './Sinnbild';
import { useTelefon } from './telefon';

/*
 * ============================================================================
 * DIE BAUSTEINE DER AKTE (ÄA-41, Entwurf v2)
 * ============================================================================
 *
 * Der Entwurf zeigt die Kundenakte; die übrigen Akten (Angebot, Auftrag,
 * Rahmen, Rechnung, Artikel, Benutzer, Buchungsvorgang) folgen ihr mit
 * denselben Teilen. Sie stehen hier, damit keine Maske eine eigene Variante
 * baut: Rumpf, Reiterleiste mit Zählern, aufklappbarer Abschnitt, Feldraster
 * mit Stift, Kennzahlenleiste, Personenkärtchen und Verlauf. Jeder ist im
 * Musterbogen zu sehen.
 */

/**
 * DER RUMPF EINER AKTE: links die Karte mit den Reitern (1,65fr), rechts die
 * Seitenleiste für Zeitliches (mindestens 320 px). Unter etwa 1100 px
 * untereinander.
 */
export function Aktenrumpf({
  haupt,
  seite,
}: {
  readonly haupt: ReactNode;
  readonly seite: ReactNode;
}): JSX.Element {
  return (
    <div className="akte">
      <section className="akte__haupt">{haupt}</section>
      <aside className="akte__seite">{seite}</aside>
    </div>
  );
}

export type Reiterwahl<K extends string> = {
  readonly kennung: K;
  readonly beschriftung: string;
  /** Der Zähler am Reiter — `undefined` heißt: keiner. */
  readonly zahl?: number | undefined;
  /**
   * Eine Warnung, die IN diesem Reiter zu behandeln ist (Auftrag #36) — etwa
   * „3 Teilnehmernamen fehlen". Am Reiter steht dafür eine Marke in Rost; der
   * Satz selbst gehört zum Namen des Reiters, damit ein Vorleseprogramm ihn
   * mitliest, und steht im Tooltip. `undefined` heißt: nichts offen.
   */
  readonly warnung?: string | undefined;
};

/**
 * DIE REITERLEISTE MIT ZÄHLERN (Entwurf: „Buchungen 4", „Kontakte 3").
 *
 * Pfeil links/rechts wechselt den Reiter, wie es das Muster `tablist`
 * verlangt; nur der gewählte steht in der Tabulatorfolge (WCAG 2.1.1).
 */
export function Reiterleiste<K extends string>({
  beschriftung,
  reiter,
  gewaehlt,
  beiWahl,
  praefix,
}: {
  readonly beschriftung: string;
  readonly reiter: readonly Reiterwahl<K>[];
  readonly gewaehlt: K;
  readonly beiWahl: (kennung: K) => void;
  /** Vorsatz der Kennungen `reiter-…` und `tafel-…`. */
  readonly praefix?: string;
}): JSX.Element {
  const leiste = useRef<HTMLDivElement>(null);
  const vor = praefix ?? '';
  const telefon = useTelefon();
  const warnungHier = reiter.find((r) => r.kennung === gewaehlt)?.warnung;
  /* Der Reiter, auf den der Fokus soll, sobald seine Wahl gezeichnet ist. */
  const fokusAuf = useRef<K | null>(null);

  /*
   * Der Fokus folgt der Wahl gleich nach dem Zeichnen — nicht erst im
   * naechsten Frame (Nachbesserung zu Auftrag CRM-42) und nicht vorher: Erst
   * dann traegt der Reiter `aria-selected`, und die Vorlesehilfe sagt ihn als
   * gewaehlt an.
   */
  useEffect(() => {
    if (fokusAuf.current === null || fokusAuf.current !== gewaehlt) return;
    fokusAuf.current = null;
    leiste.current?.querySelector<HTMLButtonElement>(`#reiter-${vor}${gewaehlt}`)?.focus();
  }, [gewaehlt, vor]);

  function beiTaste(e: ReactKeyboardEvent<HTMLDivElement>): void {
    if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
    e.preventDefault();
    const i = reiter.findIndex((r) => r.kennung === gewaehlt);
    const naechster = reiter[(i + (e.key === 'ArrowRight' ? 1 : -1) + reiter.length) % reiter.length];
    if (naechster === undefined) return;
    fokusAuf.current = naechster.kennung;
    beiWahl(naechster.kennung);
  }

  const leistenbild = (
    <div className="reiter" role="tablist" aria-label={beschriftung} ref={leiste} onKeyDown={beiTaste}>
      {reiter.map((r) => {
        const hier = r.kennung === gewaehlt;
        return (
          <button
            key={r.kennung}
            type="button"
            role="tab"
            id={`reiter-${vor}${r.kennung}`}
            aria-selected={hier}
            aria-controls={`tafel-${vor}${r.kennung}`}
            tabIndex={hier ? 0 : -1}
            className={`reiter__wahl${hier ? ' reiter__wahl--hier' : ''}`}
            onClick={() => beiWahl(r.kennung)}
          >
            {r.beschriftung}
            {/* Das Leerzeichen trennt für Vorlesehilfen „Kontakte 3" statt „Kontakte3". */}
            {r.zahl !== undefined && (
              <>
                {' '}
                <span className="reiter__zahl">{r.zahl}</span>
              </>
            )}
            {r.warnung !== undefined && (
              <>
                {' '}
                <span className="reiter__warnung" title={r.warnung}>
                  <span aria-hidden="true">!</span>
                  <span className="nur-vorlesen">({r.warnung})</span>
                </span>
              </>
            )}
          </button>
        );
      })}
    </div>
  );
  /*
   * Auftrag CRM-106 (ÄA-63): Den Tooltip der Marke erreicht auf dem Telefon
   * kein Finger. Ein Tippen auf den Reiter waehlt ihn, und seine Warnung steht
   * als Satz unter der Leiste. Vorlesehilfen hoeren sie schon im Namen des
   * Reiters, deshalb ist die Zeile fuer sie verborgen.
   */
  if (!telefon || warnungHier === undefined) return leistenbild;
  return (
    <>
      {leistenbild}
      <p className="reiter__hinweis" aria-hidden="true">
        {warnungHier}
      </p>
    </>
  );
}

/**
 * DER AUFKLAPPBARE ABSCHNITT DES ENTWURFS: Kopf 15 px / 500 auf #eef1f6, der
 * Pfeil dreht sich, der Inhalt darunter. Der Kopf ist ein Knopf mit
 * `aria-expanded` — Leertaste und Eingabe klappen ihn.
 */
export function Abschnitt({
  titel,
  zusatz,
  anfangsOffen = true,
  children,
}: {
  readonly titel: string;
  /** Eine leise Angabe rechts im Kopf: „2 offen", „nichts dokumentiert". */
  readonly zusatz?: ReactNode;
  readonly anfangsOffen?: boolean;
  readonly children: ReactNode;
}): JSX.Element {
  const [offen, setzeOffen] = useState(anfangsOffen);
  const kennung = useId();
  return (
    <section className="abschnitt" aria-labelledby={`${kennung}-kopf`}>
      <h2 className="abschnitt__titel">
        <button
          type="button"
          id={`${kennung}-kopf`}
          className="abschnitt__kopf"
          aria-expanded={offen}
          aria-controls={`${kennung}-inhalt`}
          onClick={() => setzeOffen((o) => !o)}
        >
          <Sinnbild name="caret-down" klasse={`abschnitt__pfeil${offen ? '' : ' abschnitt__pfeil--zu'}`} />
          <span className="abschnitt__name">{titel}</span>
          {zusatz !== undefined && <span className="abschnitt__zusatz">{zusatz}</span>}
        </button>
      </h2>
      <div id={`${kennung}-inhalt`} className="abschnitt__inhalt" hidden={!offen}>
        {children}
      </div>
    </section>
  );
}

/**
 * EINE UNTERGRUPPE IM ABSCHNITT (Auftrag CRM-218): eine kleine
 * Zwischenüberschrift über einem Teil der Felder. Sie klappt nicht — der
 * Abschnitt ist die Einheit, die Gruppe nur seine Gliederung.
 */
export function Feldgruppe({
  titel,
  children,
}: {
  readonly titel: string;
  readonly children: ReactNode;
}): JSX.Element {
  const kennung = useId();
  return (
    <div className="feldgruppe" role="group" aria-labelledby={kennung}>
      <h3 id={kennung} className="feldgruppe__titel k">
        {titel}
      </h3>
      {children}
    </div>
  );
}

/** Das Feldraster des Entwurfs: Spalten ab 230 px, 2 × 28 px Abstand. */
export function Feldraster({ children }: { readonly children: ReactNode }): JSX.Element {
  return <div className="feldraster">{children}</div>;
}

/**
 * EINE ZEILE DES FELDRASTERS — ANZEIGEN UND BEARBEITEN.
 *
 * Angezeigt: Beschriftung 12,5 px, Wert 14,5 px, rechts der Stift. Der Stift
 * öffnet das Feld an Ort und Stelle; es sichert beim Verlassen (M-12: „Es gibt
 * keinen Speichern-Knopf für Stammdaten") oder mit der Eingabetaste, Escape
 * verwirft. Nur dieses Feld geht an den Server, zusammen mit dem
 * Bearbeitungsstand — der Aufrufer kennt ihn.
 *
 * Ohne `beiSichern` ist die Zeile nur zu lesen: kein Stift, kein Versprechen.
 *
 * DIE HILFE STEHT BEIM BEARBEITEN. Im Raster des Entwurfs hat jede Zeile
 * dieselbe Höhe; ein Erklärsatz unter einem Wert risse die Reihe auf. Wer
 * das Feld öffnet, liest ihn — dort, wo er gebraucht wird.
 */
export function Feldzeile({
  beschriftung,
  wert,
  anzeige,
  kennung,
  hilfe,
  optionen,
  beiSichern,
}: {
  readonly beschriftung: string;
  /** Der gespeicherte Wert, wie er ins Feld kommt. */
  readonly wert: string | null;
  /** Was angezeigt wird, wenn es nicht der rohe Wert ist. */
  readonly anzeige?: ReactNode;
  /** Die Kennung des Eingabefelds — für Beschriftung und Prüfungen. */
  readonly kennung?: string;
  readonly hilfe?: string;
  /** Eine Auswahl statt eines Textfelds. */
  readonly optionen?: readonly { readonly wert: string; readonly text: string }[];
  readonly beiSichern?: (wert: string | null) => Promise<void> | void;
}): JSX.Element {
  const [bearbeiten, setzeBearbeiten] = useState(false);
  const [entwurf, setzeEntwurf] = useState(wert ?? '');
  const [laeuft, setzeLaeuft] = useState(false);
  const eigene = useId();
  const id = kennung ?? `feld-${eigene}`;
  const stift = useRef<HTMLButtonElement>(null);
  const zurueck = useRef(false);

  /* Ändert sich der Wert von außen — etwa nach einem Konflikt —, folgt das Feld. */
  useEffect(() => setzeEntwurf(wert ?? ''), [wert]);

  useEffect(() => {
    if (!bearbeiten && zurueck.current) {
      zurueck.current = false;
      stift.current?.focus();
    }
  }, [bearbeiten]);

  function sichern(neu: string): void {
    const geaendert = neu !== (wert ?? '');
    if (!geaendert || beiSichern === undefined) {
      setzeBearbeiten(false);
      return;
    }
    setzeLaeuft(true);
    void Promise.resolve(beiSichern(neu === '' ? null : neu)).finally(() => {
      setzeLaeuft(false);
      setzeBearbeiten(false);
    });
  }

  if (bearbeiten && beiSichern !== undefined) {
    return (
      <div className="feldzeile feldzeile--bearbeiten">
        <div className="feldzeile__inhalt">
          <label className="feldzeile__beschriftung" htmlFor={id}>
            {beschriftung}
          </label>
          {optionen === undefined ? (
            <input
              id={id}
              className="feld__eingabe"
              value={entwurf}
              disabled={laeuft}
              autoFocus
              onChange={(e) => setzeEntwurf(e.target.value)}
              onBlur={() => sichern(entwurf)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  zurueck.current = true;
                  sichern(entwurf);
                } else if (e.key === 'Escape') {
                  e.preventDefault();
                  e.stopPropagation();
                  zurueck.current = true;
                  setzeEntwurf(wert ?? '');
                  setzeBearbeiten(false);
                }
              }}
            />
          ) : (
            <select
              id={id}
              className="feld__eingabe"
              value={entwurf}
              disabled={laeuft}
              autoFocus
              onChange={(e) => {
                setzeEntwurf(e.target.value);
                zurueck.current = true;
                sichern(e.target.value);
              }}
              onBlur={() => setzeBearbeiten(false)}
            >
              {optionen.map((o) => (
                <option key={o.wert} value={o.wert}>
                  {o.text}
                </option>
              ))}
            </select>
          )}
          {hilfe !== undefined && <span className="feld__hilfe">{hilfe}</span>}
        </div>
      </div>
    );
  }

  const text =
    anzeige ?? (optionen?.find((o) => o.wert === wert)?.text ?? (wert === null || wert === '' ? '—' : wert));

  return (
    <div className="feldzeile">
      <div className="feldzeile__inhalt">
        <span className="feldzeile__beschriftung">{beschriftung}</span>
        <span className="feldzeile__wert">{text}</span>
      </div>
      {beiSichern !== undefined && (
        <button
          ref={stift}
          type="button"
          className="feldzeile__stift"
          aria-label={`${beschriftung} bearbeiten`}
          title={`${beschriftung} bearbeiten`}
          onClick={() => setzeBearbeiten(true)}
        >
          <Sinnbild name="pencil-simple" />
        </button>
      )}
    </div>
  );
}

/** Eine Kennzahl der Leiste im Aktenkopf. */
export type Kennzahl = {
  readonly beschriftung: string;
  readonly wert: ReactNode;
  /** Eine Frist — der Wert steht in Rost (Entwurf: „Nächste Fälligkeit"). */
  readonly frist?: boolean;
};

/**
 * DIE KENNZAHLENLEISTE DES ENTWURFS: Beschriftung 12,5 px über dem Wert
 * (500), Spalten ab 170 px. Als Beschreibungsliste — Begriff und Wert
 * gehören für eine Vorlesehilfe zusammen.
 */
export function Kennzahlenleiste({ zahlen }: { readonly zahlen: readonly Kennzahl[] }): JSX.Element {
  return (
    <dl className="kennzahlen">
      {zahlen.map((z) => (
        <div key={z.beschriftung} className={`kennzahl${z.frist === true ? ' kennzahl--frist' : ''}`}>
          <dt>{z.beschriftung}</dt>
          <dd>{z.wert}</dd>
        </div>
      ))}
    </dl>
  );
}

/** Ein <wbr> vor jedem „.": Dort darf die Zeile brechen. */
function vorPunkten(text: string): ReactNode {
  return text.split(/(?=\.)/).map((teil, i) => (
    <Fragment key={i}>
      {i > 0 && <wbr />}
      {teil}
    </Fragment>
  ));
}

/**
 * Umbruchstellen für E-Mail-Adressen und Webadressen (CRM-66). Passt die
 * Adresse nicht in die Zeile, bricht sie zuerst nach dem „@": Der Teil
 * dahinter ist ein Inline-Block und rückt als Ganzes in die nächste Zeile.
 * Erst wenn auch er nicht passt, bricht er vor einem „.". Text mit
 * Leerzeichen — Funktion, Rufnummer — bleibt, wie er ist.
 */
export function mitUmbruchstellen(text: string): ReactNode {
  if (/\s/.test(text) || !/[@.]/.test(text)) return text;
  const at = text.lastIndexOf('@');
  if (at < 0) return vorPunkten(text);
  return (
    <>
      {vorPunkten(text.slice(0, at + 1))}
      <wbr />
      <span className="personenkaertchen__domain">{vorPunkten(text.slice(at + 1))}</span>
    </>
  );
}

/**
 * DAS PERSONENKÄRTCHEN DES ENTWURFS: #f2f6fb, Kürzelkachel 40 px auf
 * Hellblau, Name (500), Funktion und Rufnummer darunter. Die Handlung
 * steht in einer eigenen Zeile unter dem Text (CRM-66).
 */
export function Personenkaertchen({
  kuerzel,
  name,
  zeilen,
  marken,
  handlung,
}: {
  readonly kuerzel: string;
  readonly name: ReactNode;
  readonly zeilen: readonly ReactNode[];
  readonly marken?: ReactNode;
  readonly handlung?: ReactNode;
}): JSX.Element {
  return (
    <div className="personenkaertchen">
      <span className="personenkaertchen__kuerzel" aria-hidden="true">
        {kuerzel}
      </span>
      <div className="personenkaertchen__text">
        <span className="personenkaertchen__name">{name}</span>
        {zeilen.map((z, i) => (
          <span key={i} className="personenkaertchen__zeile">
            {typeof z === 'string' ? mitUmbruchstellen(z) : z}
          </span>
        ))}
        {marken !== undefined && <span className="personenkaertchen__marken">{marken}</span>}
      </div>
      {handlung !== undefined && <div className="personenkaertchen__handlung">{handlung}</div>}
    </div>
  );
}

/**
 * DIE ARTEN IM VERLAUF — je Art eine Sinnbildkachel wie im Entwurf:
 * Hellblau für Aufgabe und Vorgang, Grün für Termin und Erledigtes, Grau für
 * Nachricht und Stilles, Rost für Achtung und Frist.
 */
export type Verlaufsart = 'vorgang' | 'termin' | 'erledigt' | 'still' | 'achtung';

export type Verlaufseintrag = {
  readonly kennung: string;
  readonly art: Verlaufsart;
  readonly sinnbild: NamenVon<'duotone'>;
  readonly titel: string;
  readonly detail?: string | undefined;
  /** Das Datum rechts, kurz („28.08."). */
  readonly datum: string;
  /** Der Titel führt in einen Vorgang — dann steht er in der Handlungsfarbe. */
  readonly beiWahl?: (() => void) | undefined;
  /** Die Beschreibung in Rost und 600 (Entwurf: „offen seit …"). */
  readonly dringend?: boolean;
};

/**
 * DER VERLAUF DES ENTWURFS: je Eintrag Kachel (34 px), Titel (500),
 * Beschreibung (13 px) und Datum rechts in Tabellenziffern.
 */
export function Verlauf({
  titel,
  zusatz,
  eintraege,
  leer,
}: {
  readonly titel: string;
  readonly zusatz?: string | undefined;
  readonly eintraege: readonly Verlaufseintrag[];
  readonly leer: string;
}): JSX.Element {
  const kennung = useId();
  return (
    <section className="verlauf" aria-labelledby={kennung}>
      <div className="verlauf__kopf">
        <h2 id={kennung} className="verlauf__titel">
          {titel}
        </h2>
        {zusatz !== undefined && <span className="verlauf__zusatz">{zusatz}</span>}
      </div>
      {eintraege.length === 0 ? (
        <p className="verlauf__leer">{leer}</p>
      ) : (
        <ul className="verlauf__liste">
          {eintraege.map((e) => (
            <li key={e.kennung} className="verlauf__eintrag">
              <span className={`verlauf__kachel verlauf__kachel--${e.art}`} aria-hidden="true">
                <Sinnbild name={e.sinnbild} />
              </span>
              <div className="verlauf__text">
                {e.beiWahl === undefined ? (
                  <span className="verlauf__eintragstitel">{e.titel}</span>
                ) : (
                  <button type="button" className="knopf knopf--verweis verlauf__eintragstitel" onClick={e.beiWahl}>
                    {e.titel}
                  </button>
                )}
                {e.detail !== undefined && (
                  <span className={`verlauf__detail${e.dringend === true ? ' verlauf__detail--dringend' : ''}`}>
                    {e.detail}
                  </span>
                )}
              </div>
              <span className="verlauf__datum">{e.datum}</span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
